import React, { useEffect, useState, useCallback } from "react";
import "./../../../../../assets/css/Dashboard/ServicesKanban.scss";
import useTitanApp from "../../../../../hooks/useTitanApp";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Skeleton } from "@mui/material";
import {
  getDueName,
  getTotalServicesDueCount,
  getTotalDaysLeft,
} from "../../../../../data/getStartDateForStages";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import ServiceItemList from "./ServiceItemList";
import InfiniteScroll from "react-infinite-scroll-component";
import debounce from "lodash.debounce";

function ServicesKanbanAccordian({
  service,
  currentIndex,
  stage,
  smsQueue,
  reSmsQueue,
  interventionQueue,
  declineQueue,
  rescheduleQueue,
  pdsQueue,
  orbitQueue,
  setCurrentSmsQueue,
  setCurrentReSmsQueue,
  setCurrentInterventionQueue,
  setCurrentDeclineQueue,
  setCurrentRescheduleQueue,
  setCurrentPdsQueue,
  setCurrentOrbitQueue,
  checksCount,
  setChecksCount,
  servicesUpdatedData,
  setShowPatientContactModal,
  setEligibilityScroll,
  setContactServiceID,
}) {
  const { pharmacyServiceState, appState } = useTitanApp();
  const { darkMode } = appState || {};
  const { isPharmacyServiceLoading } = pharmacyServiceState;
  const privateServices = service || {};
  const [visibleServices, setVisibleServices] = useState({});
  const [hasMore, setHasMore] = useState(true);

  const groupedClinicalServices = (
    privateServices?.clinicalServices || []
  ).reduce((acc, clinicalService) => {
    const { patientId, clinicalServiceDate } = clinicalService;
    acc[patientId] = acc[patientId] || {};
    const dateKey = new Date(clinicalServiceDate).toISOString().split("T")[0];
    acc[patientId][dateKey] = acc[patientId][dateKey] || [];
    acc[patientId][dateKey].push(clinicalService);
    return acc;
  }, {});

  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [currentDateIndices, setCurrentDateIndices] = useState({});

  const fetchMoreData = useCallback(() => {
    const groupKeys = Object.keys(groupedClinicalServices || {});
    if (currentGroupIndex >= groupKeys.length) {
      setHasMore(false);
      return;
    }

    const updatedServices = { ...visibleServices };
    let newGroupIndex = currentGroupIndex;
    let loadCount = 0;

    while (newGroupIndex < groupKeys.length && loadCount < 20) {
      const groupKey = groupKeys[newGroupIndex];
      const dates = Object.keys(groupedClinicalServices[groupKey] || {});
      const currentDateIndex = currentDateIndices[groupKey] || 0;

      for (let i = currentDateIndex; i < dates.length; i++) {
        const date = dates[i];
        if (!updatedServices[groupKey]) updatedServices[groupKey] = {};
        if (!updatedServices[groupKey][date]) {
          updatedServices[groupKey][date] =
            groupedClinicalServices[groupKey][date];
          loadCount += groupedClinicalServices[groupKey][date].length;
          if (loadCount >= 20) {
            setCurrentDateIndices((prev) => ({ ...prev, [groupKey]: i + 1 }));
            break;
          }
        }
      }

      if (loadCount < 20) newGroupIndex++;
    }

    setVisibleServices(updatedServices);
    setCurrentGroupIndex(newGroupIndex);
    if (newGroupIndex >= groupKeys.length) setHasMore(false);
  }, [
    groupedClinicalServices,
    currentGroupIndex,
    currentDateIndices,
    visibleServices,
  ]);

  const debouncedFetchData = useCallback(debounce(fetchMoreData, 500), [
    fetchMoreData,
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  useEffect(() => {
    const clinicalServices = service?.clinicalServices;
    if (!clinicalServices || clinicalServices.length === 0) return;

    const freshById = new Map();
    clinicalServices.forEach((cs) => freshById.set(cs._id, cs));

    setVisibleServices((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      let changed = false;
      const updated = {};
      for (const [pid, dates] of Object.entries(prev)) {
        updated[pid] = {};
        for (const [dk, services] of Object.entries(dates)) {
          updated[pid][dk] = services.map((s) => {
            const fresh = freshById.get(s._id);
            if (fresh && fresh !== s) {
              changed = true;
              return fresh;
            }
            return s;
          });
        }
      }
      return changed ? updated : prev;
    });
  }, [service?.clinicalServices]);

  // ---------- helper to parse daysLeft output into a number ----------
  // returns: numeric days (0, 1, 2, ...) or Infinity for Overdue/unparseable (so they go last)
  const parseDaysLeft = (daysLeftLabel) => {
    if (daysLeftLabel == null) return Infinity;
    if (typeof daysLeftLabel === "number") return daysLeftLabel;
    const str = String(daysLeftLabel)
      .trim()
      .toLowerCase();
    if (!str) return Infinity;
    if (str.includes("overdue")) return Infinity;
    // match first integer
    const m = str.match(/-?\d+/);
    if (m) {
      const n = parseInt(m[0], 10);
      // if negative (overdue in numeric sense), treat as -n (we want overdue last)
      return Number.isFinite(n) ? n : Infinity;
    }
    return Infinity;
  };

  return (
    <Accordion key={currentIndex} className="dueDate_accordion" defaultExpanded>
      <AccordionSummary
        key={currentIndex}
        expandIcon={
          <ExpandMoreIcon fontSize="14" className="expandIcon_dueDate" />
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div>
          <p className="service_title service_duedate_title">
            {isPharmacyServiceLoading ? (
              <Skeleton
                sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                height={30}
                animation="wave"
              />
            ) : (
              `${getDueName(service?.dueType)} ${getTotalServicesDueCount(
                service,
                stage?.stageName,
                service?.dueType,
              )}`
            )}
          </p>
        </div>
      </AccordionSummary>
      <AccordionDetails key={currentIndex} className="data-box-container">
        <InfiniteScroll
          className={`infinite-scroll-${stage?.stageName}`}
          dataLength={Object.keys(visibleServices).length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollThreshold={0.8}
          style={{ overflow: "auto" }}
        >
          {(() => {
            const allGroups = Object.values(
              visibleServices || {},
            ).flatMap((patientGroups) => Object.values(patientGroups || {}));
            if (!allGroups || allGroups.length === 0) return null;
            const groupsWithDays = allGroups.map((group) => {
              const rep = group && group[0];
              const rawLabel = getTotalDaysLeft(rep, stage?.stageName);
              const parsed = parseDaysLeft(rawLabel);
              return { group, rep, rawLabel, parsed };
            });

            groupsWithDays.sort((a, b) => {
              const va = a.parsed;
              const vb = b.parsed;

              return va - vb;
            });

            return groupsWithDays.map(({ group }, groupIndex) => {
              const sortedGroupItems = [...(group || [])].sort((a, b) => {
                const aLabel = getTotalDaysLeft(a, stage?.stageName);
                const bLabel = getTotalDaysLeft(b, stage?.stageName);
                const aNum = parseDaysLeft(aLabel);
                const bNum = parseDaysLeft(bLabel);
                return aNum - bNum;
              });

              if ((sortedGroupItems?.length || 0) > 1) {
                const representativeItem = sortedGroupItems[0];
                return (
                  <div
                    className="stacked-cards-container"
                    key={`group-${groupIndex}-${representativeItem?._id ||
                      groupIndex}`}
                  >
                    {isPharmacyServiceLoading ? (
                      <Skeleton
                        key={`skeleton-${groupIndex}-0`}
                        sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                        height={200}
                        animation="wave"
                      />
                    ) : (
                      <div
                        className="check_service"
                        key={`svc-${groupIndex}-0-${representativeItem?._id}`}
                      >
                        <ServiceItemList
                          service={representativeItem}
                          stage={stage}
                          index={0}
                          group={group}
                          smsQueue={smsQueue}
                          reSmsQueue={reSmsQueue}
                          interventionQueue={interventionQueue}
                          declineQueue={declineQueue}
                          rescheduleQueue={rescheduleQueue}
                          pdsQueue={pdsQueue}
                          orbitQueue={orbitQueue}
                          servicesUpdatedData={servicesUpdatedData}
                          setCurrentSmsQueue={setCurrentSmsQueue}
                          setCurrentReSmsQueue={setCurrentReSmsQueue}
                          setCurrentInterventionQueue={
                            setCurrentInterventionQueue
                          }
                          setCurrentDeclineQueue={setCurrentDeclineQueue}
                          setCurrentRescheduleQueue={setCurrentRescheduleQueue}
                          setCurrentPdsQueue={setCurrentPdsQueue}
                          setCurrentOrbitQueue={setCurrentOrbitQueue}
                          checksCount={checksCount}
                          setChecksCount={setChecksCount}
                          setShowPatientContactModal={
                            setShowPatientContactModal
                          }
                          setEligibilityScroll={setEligibilityScroll}
                          setContactServiceID={setContactServiceID}
                        />
                      </div>
                    )}
                    <div className="bottom-stacked"></div>
                    <div className="bottom-stacked"></div>
                  </div>
                );
              }
              const serviceItem = sortedGroupItems[0];
              return isPharmacyServiceLoading ? (
                <Skeleton
                  key={`skeleton-single-${groupIndex}`}
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                  height={180}
                  animation="wave"
                />
              ) : (
                <div
                  className="check_service"
                  key={`single-${groupIndex}-${serviceItem?._id || groupIndex}`}
                >
                  <ServiceItemList
                    service={serviceItem}
                    stage={stage}
                    index={0}
                    group={group}
                    smsQueue={smsQueue}
                    reSmsQueue={reSmsQueue}
                    interventionQueue={interventionQueue}
                    declineQueue={declineQueue}
                    rescheduleQueue={rescheduleQueue}
                    pdsQueue={pdsQueue}
                    orbitQueue={orbitQueue}
                    servicesUpdatedData={servicesUpdatedData}
                    setCurrentSmsQueue={setCurrentSmsQueue}
                    setCurrentReSmsQueue={setCurrentReSmsQueue}
                    setCurrentInterventionQueue={setCurrentInterventionQueue}
                    setCurrentDeclineQueue={setCurrentDeclineQueue}
                    setCurrentRescheduleQueue={setCurrentRescheduleQueue}
                    setCurrentPdsQueue={setCurrentPdsQueue}
                    setCurrentOrbitQueue={setCurrentOrbitQueue}
                    checksCount={checksCount}
                    setChecksCount={setChecksCount}
                    setShowPatientContactModal={setShowPatientContactModal}
                    setEligibilityScroll={setEligibilityScroll}
                    setContactServiceID={setContactServiceID}
                  />
                </div>
              );
            });
          })()}
        </InfiniteScroll>
      </AccordionDetails>
    </Accordion>
  );
}

export default React.memo(ServicesKanbanAccordian);
